import { useTranslation } from 'react-i18next'
import { baseQty, displayRate, fmt, type Order } from '../api'

export type BookRow = { key: string; rate: number; amount: number; cum: number; maker: string; side: 'buy' | 'sell' }

/**
 * Split the flat order list into asks and bids for one market, each sorted
 * best-price-first with a running cumulative size. Buy rows store the quote
 * amount on chain, so `baseQty` converts them to the base unit; both sides then
 * measure the same thing and the depth bars are comparable.
 */
export function bookSides(orders: Order[]) {
  const build = (side: 'buy' | 'sell') => {
    const rows = orders
      .filter(o => o.side === side)
      .map(o => ({ key: o.chain_key, rate: displayRate(o.price, o.side), amount: baseQty(o), maker: o.maker_account ?? '', side }))
      .filter(r => r.rate > 0 && r.amount > 0)
      // asks: cheapest first. bids: dearest first.
      .sort((a, b) => (side === 'sell' ? a.rate - b.rate : b.rate - a.rate))
    let cum = 0
    return rows.map(r => ({ ...r, cum: (cum += r.amount) })) as BookRow[]
  }
  const asks = build('sell')
  const bids = build('buy')
  const best = { ask: asks[0]?.rate ?? 0, bid: bids[0]?.rate ?? 0 }
  return {
    asks, bids, best,
    spread: best.ask > 0 && best.bid > 0 ? best.ask - best.bid : 0,
    maxCum: Math.max(asks.at(-1)?.cum ?? 0, bids.at(-1)?.cum ?? 0, 1),
  }
}

type Props = {
  orders: Order[]
  base: string
  quote: string
  depth?: number
  lastPrice: number
  onPick: (rate: number) => void
}

export default function OrderBook({ orders, base, quote, depth = 8, lastPrice, onPick }: Props) {
  const { t } = useTranslation()
  const { asks, bids, spread, maxCum } = bookSides(orders)

  const Row = ({ r }: { r: BookRow }) => (
    <button
      type="button"
      className={`book-row ${r.side === 'sell' ? 'ask' : 'bid'}`}
      style={{ ['--depth' as string]: `${Math.min(100, (r.cum / maxCum) * 100)}%` }}
      onClick={() => onPick(r.rate)}
      title={`${t('price')} ${fmt(r.rate)} · ${t('qty')} ${fmt(r.amount, 4)} ${base}`}
    >
      <span className="price num">{fmt(r.rate)}</span>
      <span className="num right">{fmt(r.amount, 4)}</span>
      <span className="maker right">{r.maker}</span>
    </button>
  )

  return (
    <div className="panel">
      <div className="panel-head">
        <span className="grow">{t('book')}</span>
        <span className="chip">{base}/{quote}</span>
      </div>

      <div className="book">
        <div className="book-head">
          <span>{t('price')} ({quote})</span>
          <span className="right">{t('qty')} ({base})</span>
          <span className="right">{t('maker')}</span>
        </div>

        {asks.length === 0 && bids.length === 0 ? (
          <div className="empty">{t('noOrders')}</div>
        ) : (
          <>
            {/* asks render column-reverse: cheapest ask sits closest to the spread */}
            <div className="book-side asks">
              {asks.length === 0
                ? <div className="book-none">{t('noAsks')}</div>
                : asks.slice(0, depth).map(r => <Row key={r.key} r={r} />)}
            </div>

            <div className="book-spread">
              <b className={lastPrice > 0 ? 'up' : undefined}>{lastPrice > 0 ? fmt(lastPrice) : '—'}</b>
              <span className="muted" style={{ fontSize: 12 }}>
                {t('spread')} {spread > 0 ? fmt(spread) : '—'}
              </span>
            </div>

            <div className="book-side">
              {bids.length === 0
                ? <div className="book-none">{t('noBids')}</div>
                : bids.slice(0, depth).map(r => <Row key={r.key} r={r} />)}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
